/**
 * ConfirmDialog — Context, types, hook.
 *
 * Le composant Provider vit dans `components/feedback/ConfirmDialog.tsx`.
 * Cette regle remplace `window.confirm/alert/prompt` (bloques par
 * `no-restricted-globals` en ESLint).
 *
 * Usage :
 *   const { confirm, alert, prompt } = useConfirm()
 *
 *   if (await confirm('Archiver cette œuvre ?')) { ... }
 *   if (await confirm({
 *     title: 'Suppression',
 *     message: 'Action irreversible.',
 *     variant: 'danger',
 *     confirmText: 'Supprimer',
 *   })) { ... }
 *
 *   await alert('Generation lancee')
 *   const value = await prompt({ message: 'Nouveau titre', defaultValue: '...' })
 *
 * Adapte de scapino-portal/frontend/src/components/confirm-dialog-context.ts.
 */

import { createContext, useContext } from 'react'

export type DialogVariant = 'default' | 'danger'

export interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: DialogVariant
}

export interface AlertOptions {
  title?: string
  message: string
}

export interface PromptOptions {
  title?: string
  message: string
  defaultValue?: string
  placeholder?: string
  confirmText?: string
  cancelText?: string
}

export type DialogState =
  | { type: 'confirm'; options: ConfirmOptions; resolve: (v: boolean) => void }
  | { type: 'alert'; options: AlertOptions; resolve: () => void }
  | { type: 'prompt'; options: PromptOptions; resolve: (v: string | null) => void }

export interface ConfirmContextValue {
  confirm: (opts: string | ConfirmOptions) => Promise<boolean>
  alert: (opts: string | AlertOptions) => Promise<void>
  prompt: (opts: string | PromptOptions) => Promise<string | null>
}

export const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmDialogProvider>')
  return ctx
}
